import {
  createHashHistory,
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { AnimatePresence } from 'framer-motion';
import { PageTransition } from './components/layout/PageTransition';
import { AppShell } from './components/layout/AppShell';
import { Skeleton } from './components/ui/Skeleton';
import { RequireAuth, useIsSignedIn } from './auth/clerkAuth';
import { usePlanGate } from './hooks/usePlanGate';

// Create hash history for static deployment compatibility
const hashHistory = createHashHistory();

// Lazy load all page components for code splitting
const HomePage = lazy(() => import('./pages/HomePage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const AssistantPage = lazy(() => import('./pages/AssistantPage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const KnowledgePage = lazy(() => import('./pages/KnowledgePage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const TasksPage = lazy(() => import('./pages/TasksPage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const ProjectWorkspacePage = lazy(() => import('./pages/ProjectWorkspacePage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const SuggestionsPage = lazy(() => import('./pages/SuggestionsPage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const ConnectionsPage = lazy(() => import('./pages/ConnectionsPage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(module => ({
  default: () => <PageTransition><module.default /></PageTransition>
})));

// Public marketing pages (no auth, no app chrome).
const LandingPage = lazy(() => import('./pages/marketing/LandingPage'));
const PricingPage = lazy(() => import('./pages/marketing/PricingPage'));
const DocsPage = lazy(() => import('./pages/marketing/DocsPage'));
const CheckoutPage = lazy(() => import('./pages/marketing/CheckoutPage'));

// Paths that render publicly, outside the auth + app shell.
const PUBLIC_PREFIXES = ['/pricing', '/docs', '/checkout'];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Skeleton loader component for route transitions
const RouteLoadingSkeleton = () => (
  <div className="flex items-center justify-center min-h-screen p-4">
    <div className="w-full max-w-4xl space-y-4">
      <Skeleton className="h-12 w-3/4" />
      <Skeleton className="h-8 w-1/2" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  </div>
);

/** Gate for authenticated app routes: sign-in + plan gate + app chrome. */
function AppGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useIsSignedIn();
  const navigate = useNavigate();
  const { loading, needsPlan } = usePlanGate(isLoaded && isSignedIn);

  useEffect(() => {
    if (isSignedIn && !loading && needsPlan) {
      navigate({ to: '/pricing' });
    }
  }, [isSignedIn, loading, needsPlan, navigate]);

  return (
    <RequireAuth>
      <AppShell>
        <AnimatePresence mode="wait">
          <Suspense fallback={<RouteLoadingSkeleton />}>
            {isSignedIn && (loading || needsPlan) ? <RouteLoadingSkeleton /> : children}
          </Suspense>
        </AnimatePresence>
      </AppShell>
    </RequireAuth>
  );
}

// Create root route: splits public marketing from gated app.
const rootRoute = createRootRoute({
  component: () => {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const { isSignedIn } = useIsSignedIn();

    // Public marketing routes + the signed-out landing at "/" render bare.
    if (isPublicPath(pathname) || (pathname === '/' && !isSignedIn)) {
      return (
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <Outlet />
        </Suspense>
      );
    }

    return (
      <AppGate>
        <Outlet />
      </AppGate>
    );
  },
});

// Index: landing for signed-out visitors, the assistant chat once signed in.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: () => {
    const { isSignedIn } = useIsSignedIn();
    return isSignedIn ? (
      <PageTransition><AssistantPage /></PageTransition>
    ) : (
      <LandingPage />
    );
  },
});

// Public marketing routes
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs',
  component: DocsPage,
});

const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  component: CheckoutPage,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: HomePage,
});

const knowledgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/knowledge',
  component: KnowledgePage,
});

const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  component: TasksPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: ProjectsPage,
});

const projectWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/workspace',
  component: ProjectWorkspacePage,
});

const suggestionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/suggestions',
  component: SuggestionsPage,
});

const connectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connections',
  component: ConnectionsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
});

// Create a catch-all route for 404s that redirects to home
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: () => {
    // Redirect to home on invalid routes
    if (typeof window !== 'undefined') {
      window.location.hash = '#/';
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Redirecting to home...</div>
      </div>
    );
  },
});

// Create route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  pricingRoute,
  docsRoute,
  checkoutRoute,
  homeRoute,
  knowledgeRoute,
  tasksRoute,
  projectsRoute,
  projectWorkspaceRoute,
  suggestionsRoute,
  connectionsRoute,
  profileRoute,
  notFoundRoute,
]);

// Create router instance with hash history and preload configuration
export const router = createRouter({
  routeTree,
  history: hashHistory,
  defaultPreload: 'intent', // Preload on hover/focus
  defaultPreloadDelay: 50, // 50ms delay before preloading
  defaultPendingComponent: RouteLoadingSkeleton,
  defaultErrorComponent: ({ error }) => (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <h1 className="text-2xl font-bold text-destructive mb-2">Something went wrong</h1>
      <p className="text-muted-foreground mb-4">{error.message}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Reload page
      </button>
    </div>
  ),
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
